<template>
    <v-dialog v-model="submitDialog" max-width="700" persistent>
        <template v-slot:activator="{ on, attrs }">
            <slot name="dialogactivator" :on="on" :attrs="attrs">
                <v-btn v-bind="attrs" v-on="on" color="primary">Hand-in</v-btn>
            </slot>
        </template>
        <v-card flat>
            <v-card-title class="flex-nowrap align-start">
                <div class="d-flex align-center w-100">
                    <span class="secondary--text ml-1">{{ bundleData.long_id }}</span>
                    <v-spacer></v-spacer>
                </div>

                <v-spacer></v-spacer>
                <v-btn :disabled="assignmentLoading" @click="submitDialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>

            <v-card-text>
                <div class="d-flex flex-column mt-3">
                    <v-alert type="info" text class="mb-4">
                        The content of the following files / folders will be handed in, please make sure they exist otherwise hand-in will not succeed!
                    </v-alert>
                    <v-expansion-panels :value="[0]" flat light multiple class="w-100 mb-4">
                        <v-expansion-panel>
                            <v-expansion-panel-header class="px-0">
                                <span class="secondary--text font-weight-bold">Files ({{ bundleData.objects.files.length }})</span>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-list class="pa-0" dense light>
                                    <v-list-item-group class="pa-0 ma-0" color="secondary">
                                        <v-list-item style="max-height: 5px" v-for="file in bundleData.objects.files" :key="file" class="pa-0">
                                            <v-list-item-avatar>
                                                <v-icon small>mdi-file-outline</v-icon>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title class="subtitle-2">{{ file.slice(6) }}</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list-item-group>
                                </v-list>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    <span class="subtitle-2 mb-2">
                        Please provide a hand-in identifier (e.g. exam code, email address, etc..) that is in line with your course policy and allows the
                        instructor to match your work with your identifier. If unsure, please discuss the appropriate identifier with your instructor.
                    </span>
                    <v-text-field autofocus class="mt-3" label="Hand-in identifier" v-model="handinIdentifier" outlined></v-text-field>
                </div>
                <v-alert type="error" text v-if="bundleSizeInfo?.status === 'fail'">
                    <div v-if="bundleSizeInfo.fail_reason === 'submission_size_too_large'">
                        The size of your hand-in exceeds the {{ bundleData.handin_limit_bytes / (1000 * 1000) }} MB limit. Please check your files and remove
                        unneeded files such as data files, core dump files, hidden files, etc:
                        <v-list dense light>
                            <v-list-item v-for="file in filesSortedBySize" :key="file.name">
                                <v-list-item-content>
                                    <v-list-item-title class="subtitle-2 error--text">
                                        {{ file.name }}
                                    </v-list-item-title>
                                </v-list-item-content>
                                <v-list-item-action class="error--text">
                                    {{ humanFileSize(file.size_bytes) }}
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>
                    </div>
                    <div v-if="bundleSizeInfo?.fail_reason === 'missing_files'">
                        <div>The following files are missing and must be included in your submission:</div>
                        <v-list dense light>
                            <v-list-item v-for="file in missingFiles" :key="file.name">
                                <v-list-item-content>
                                    <v-list-item-title class="subtitle-2 error--text">{{ file.name }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </div>
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn
                    :disabled="!handinIdentifier || bundleSizeInfo?.status === 'fail' || $isError(bundleSizeInfo)"
                    :loading="assignmentLoading"
                    color="primary"
                    text
                    @click="submitAssignment(handinIdentifier, assignmentShortId, bundleData.bid)">
                    Hand-in
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { assignmentMethods } from '@/mixins/assignments'
import { makeShortId, humanFileSize } from '@/utils'
import { orderBy } from 'lodash'

export default {
    name: 'SnapshotAssignmentSubmitDialog',
    mixins: [assignmentMethods],
    props: {
        bundleData: Object
    },
    data() {
        return {
            submitDialog: false,
            handinIdentifier: '',
            bundleSizeInfo: null
        }
    },
    computed: {
        assignmentShortId() {
            if (this.handinIdentifier) {
                return makeShortId(this.handinIdentifier, 48)
            }
            return ''
        },
        currentInstance() {
            return this.$store.getters['instanceStore/instanceLongNameById'](this.$route.params.iid)
        },
        missingFiles() {
            if (!this.bundleSizeInfo?.files) return []

            const missingFiles = []
            this.bundleSizeInfo.files.forEach(file => {
                if (!file.size_bytes) {
                    missingFiles.push(file)
                }
            })
            return missingFiles
        },
        filesSortedBySize() {
            if (!this.bundleSizeInfo?.files) return []

            return orderBy(this.bundleSizeInfo.files, ['size_bytes'], ['desc'])
        }
    },
    mounted() {
        this.handinIdentifier = this.currentInstance
    },
    methods: {
        async checkBundleSize() {
            try {
                const { data } = await this.$axios.get(`/bundles/${this.bundleData.bid}/validate?iid=${this.$route.params.iid}`)
                this.bundleSizeInfo = data
            } catch (error) {
                console.error('Error getting bundle size:', error)
                this.bundleSizeInfo = error
            }
        },
        humanFileSize(bytes, si) {
            return humanFileSize(bytes, si)
        }
    },
    watch: {
        assignmentLoading(nextVal) {
            if (!nextVal && this.submitDialog) {
                this.handinIdentifier = ''
                this.submitDialog = false
            }
        },
        currentInstance(nextVal) {
            this.handinIdentifier = nextVal
        },
        submitDialog(open) {
            if (open) {
                this.checkBundleSize()
            }
        }
    }
}
</script>
